import React from "react";
import {
  ChartPie,
  Building2,
  Bed,
  DollarSign,
  Settings,
  TrendingUp,
  Eye,
  Target,
  Building,
  BarChart2,
  Hotel,
  Pool,
  Utensils,
  Wifi,
  Dumbbell,
  Coffee,
} from "lucide-react";

const CompetitorManagerTab = ({
  marketShare,
  competitors,
  marketingCampaigns,
  money,
  launchMarketingCampaign,
  selectedTab,
}) => {
  // Function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg
          key={`full-${i}`}
          className="w-4 h-4 text-yellow-400 fill-current"
          viewBox="0 0 24 24"
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <svg
          key="half"
          className="w-4 h-4 text-yellow-400 fill-current"
          viewBox="0 0 24 24"
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" />
          <path
            fill="#e5e7eb"
            d="M12 17.27V2l2.81 6.63 7.19.61-5.46 4.73 1.64 7.03L12 17.27z"
          />
        </svg>
      );
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg
          key={`empty-${i}`}
          className="w-4 h-4 text-gray-300 fill-current"
          viewBox="0 0 24 24"
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      );
    }

    return <div className="flex">{stars}</div>;
  };

  return (
    selectedTab === "competitors" && (
      <div className="max-w-full overflow-hidden">
        <h2 className="text-xl md:text-2xl font-bold mb-4 border-b pb-2">
          Market Competition
        </h2>

        {/* Market Analysis Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 flex items-center">
            <ChartPie className="w-5 h-5 mr-2 text-blue-600" />
            Market Analysis
          </h3>
          <div className="p-4 md:p-6 bg-blue-50 rounded-lg shadow-sm">
            <div className="flex justify-between mb-3 items-center">
              <span className="font-medium text-gray-700">
                Your Market Share:
              </span>
              <span className="font-bold text-blue-700 text-lg">
                {marketShare}%
              </span>
            </div>

            {/* Market Share Bar Graph */}
            <div className="mb-5">
              <div className="flex h-10 rounded-lg overflow-hidden shadow-inner">
                <div
                  className="bg-blue-600 text-white text-xs flex items-center justify-center transition-all duration-500"
                  style={{ width: `${marketShare}%` }}
                >
                  {marketShare >= 8 ? `You: ${marketShare}%` : ""}
                </div>
                {competitors.map((competitor, index) => {
                  // Calculate competitors' market shares
                  const totalRemaining = 100 - marketShare;
                  const totalRooms = competitors.reduce(
                    (sum, c) => sum + c.rooms * c.occupancyRate,
                    0
                  );
                  const share =
                    totalRooms > 0
                      ? ((competitor.rooms * competitor.occupancyRate) /
                          totalRooms) *
                        totalRemaining
                      : totalRemaining / competitors.length;

                  return (
                    <div
                      key={index}
                      className={`${
                        index % 4 === 0
                          ? "bg-green-500"
                          : index % 4 === 1
                          ? "bg-purple-500"
                          : index % 4 === 2
                          ? "bg-amber-500"
                          : "bg-blue-500"
                      } text-white text-xs flex items-center justify-center transition-all duration-500`}
                      style={{ width: `${share}%` }}
                    >
                      {share >= 8 ? `${Math.round(share)}%` : ""}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap text-sm mt-2 gap-y-2">
                <div className="mr-4 flex items-center">
                  <div className="w-3 h-3 bg-blue-600 mr-1 rounded-sm"></div>
                  <span>Your Hotel</span>
                </div>
                {competitors.map((competitor, index) => (
                  <div key={index} className="mr-4 flex items-center">
                    <div
                      className={`w-3 h-3 ${
                        index % 4 === 0
                          ? "bg-green-500"
                          : index % 4 === 1
                          ? "bg-purple-500"
                          : index % 4 === 2
                          ? "bg-amber-500"
                          : "bg-blue-500"
                      } mr-1 rounded-sm`}
                    ></div>
                    <span>{competitor.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-sm text-gray-600 bg-white p-3 rounded-md">
              <p className="mb-1">
                <span className="font-medium">Market Analysis:</span> Market
                share is calculated based on occupancy, reputation, and
                marketing efforts.
              </p>
              <p>
                <span className="font-medium">Strategy Tip:</span> To increase
                your market share, improve your hotel's reputation, run
                marketing campaigns, and add more rooms.
              </p>
            </div>
          </div>
        </div>

        {/* Competitor List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 flex items-center">
            <Hotel className="w-5 h-5 mr-2 text-blue-600" />
            Competitor Hotels
          </h3>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
            {competitors.map((competitor, index) => (
              <CompetitorCard
                key={index}
                competitor={competitor}
                index={index}
                renderStars={renderStars}
                marketingCampaigns={marketingCampaigns}
                money={money}
                launchMarketingCampaign={launchMarketingCampaign}
              />
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <StrategyTipsSection />
      </div>
    )
  );
};

// CompetitorCard component with improved design and hotel icons
const CompetitorCard = ({
  competitor,
  index,
  renderStars,
  marketingCampaigns,
  money,
  launchMarketingCampaign,
}) => {
  // Helper function to get hotel category icon
  const getHotelCategoryIcon = (price) => {
    if (price > 200) {
      return (
        <svg
          className="w-6 h-6 text-amber-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
          />
        </svg>
      );
    } else if (price > 100) {
      return (
        <svg
          className="w-6 h-6 text-blue-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      );
    } else {
      return (
        <svg
          className="w-6 h-6 text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      );
    }
  };

  // Get facility icons
  const getFacilityIcon = (facility) => {
    const icons = {
      Pool: (
        <svg
          className="w-3 h-3"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
            clipRule="evenodd"
          />
        </svg>
      ),
      Spa: (
        <svg
          className="w-3 h-3"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
          <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
          <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
        </svg>
      ),
      Gym: (
        <svg
          className="w-3 h-3"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
      Restaurant: (
        <svg
          className="w-3 h-3"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
        </svg>
      ),
      Conference: (
        <svg
          className="w-3 h-3"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
      ),
      WiFi: (
        <svg
          className="w-3 h-3"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.242 0 1 1 0 01-1.415-1.415 5 5 0 017.072 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
    };

    return icons[facility] || <Settings className="w-3 h-3" />;
  };

  // Get gradient background based on hotel category
  const getGradientClass = (price) => {
    if (price > 200) {
      return "from-amber-50 to-amber-100";
    } else if (price > 100) {
      return "from-blue-50 to-blue-100";
    } else {
      return "from-green-50 to-green-100";
    }
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all bg-white border-gray-200 hover:border-gray-300`}
    >
      {/* Hotel Header with improved design */}
      <div
        className={`p-4 border-b bg-gradient-to-r ${getGradientClass(
          competitor.avgRoomPrice
        )}`}
      >
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            {getHotelCategoryIcon(competitor.avgRoomPrice)}
            <h4
              className="font-bold text-lg ml-2 truncate"
              title={competitor.name}
            >
              {competitor.name}
            </h4>
          </div>
          <div className="flex flex-col items-end">
            <div className="flex">{renderStars(competitor.reputation)}</div>
            <span className="text-sm text-gray-500 mt-1">
              ({competitor.reputation.toFixed(1)} rating)
            </span>
          </div>
        </div>
      </div>

      {/* Hotel info with icons and better styling */}
      <div className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {/* Room info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm font-medium text-gray-700 flex items-center mb-1">
              <Bed className="w-4 h-4 mr-1 text-blue-500" />
              Accommodation
            </div>
            <div className="font-semibold text-lg">
              {competitor.rooms} rooms
            </div>
            <div className="flex items-center mt-1">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    competitor.occupancyRate > 0.7
                      ? "bg-green-500"
                      : competitor.occupancyRate < 0.5
                      ? "bg-red-500"
                      : "bg-yellow-500"
                  }`}
                  style={{ width: `${competitor.occupancyRate * 100}%` }}
                ></div>
              </div>
              <span
                className={`ml-2 text-sm ${
                  competitor.occupancyRate > 0.7
                    ? "text-green-600 font-medium"
                    : competitor.occupancyRate < 0.5
                    ? "text-red-600 font-medium"
                    : "text-yellow-600 font-medium"
                }`}
              >
                {Math.round(competitor.occupancyRate * 100)}%
              </span>
            </div>
          </div>

          {/* Price info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm font-medium text-gray-700 flex items-center mb-1">
              <DollarSign className="w-4 h-4 mr-1 text-green-500" />
              Pricing
            </div>
            <div className="font-semibold text-lg">
              ${competitor.avgRoomPrice}
            </div>
            <div
              className="text-sm mt-1 px-2 py-0.5 inline-block rounded-full text-white font-medium"
              style={{
                backgroundColor:
                  competitor.avgRoomPrice > 200
                    ? "#f59e0b"
                    : competitor.avgRoomPrice > 100
                    ? "#3b82f6"
                    : "#10b981",
              }}
            >
              {competitor.avgRoomPrice > 200
                ? "Luxury"
                : competitor.avgRoomPrice > 100
                ? "Mid-range"
                : "Budget"}
            </div>
          </div>

          {/* Facilities */}
          <div className="sm:col-span-1 col-span-2 bg-gray-50 p-3 rounded-lg">
            <div className="text-sm font-medium text-gray-700 flex items-center mb-2">
              <svg
                className="w-4 h-4 mr-1 text-purple-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
              Amenities
            </div>
            <div className="flex flex-wrap gap-1.5">
              {competitor.facilities.map((facility, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 text-xs bg-white border border-gray-200 text-gray-700 rounded-full flex items-center"
                >
                  {getFacilityIcon(facility)}
                  <span className="ml-1">{facility}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Marketing section with action button */}
      <div className="p-4 bg-gray-50 border-t">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
          <div className="text-sm bg-white px-3 py-2 rounded-md border border-gray-200">
            {competitor.marketingActive ? (
              <span className="text-purple-700 flex items-center">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span className="font-medium">Active marketing campaign!</span>
              </span>
            ) : (
              <span className="text-gray-500 flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  ></path>
                </svg>
                No active marketing
              </span>
            )}
          </div>

          <div>
            <button
              onClick={() => launchMarketingCampaign("competitive")}
              disabled={
                marketingCampaigns.find((c) => c.id === "competitive")
                  ?.active ||
                money <
                  marketingCampaigns.find((c) => c.id === "competitive")?.cost
              }
              className={`px-4 py-2 rounded-lg text-sm w-full sm:w-auto flex items-center justify-center transition-colors ${
                marketingCampaigns.find((c) => c.id === "competitive")
                  ?.active ||
                money <
                  marketingCampaigns.find((c) => c.id === "competitive")?.cost
                  ? "bg-gray-200 text-gray-500"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
            >
              <Target className="w-4 h-4 mr-1.5" />
              Target Their Customers ($
              {marketingCampaigns.find((c) => c.id === "competitive")?.cost})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Strategy Tips Section as a separate component
const StrategyTipsSection = () => {
  return (
    <div className="bg-blue-50 p-4 md:p-5 rounded-lg shadow-sm border border-blue-100">
      <h3 className="font-bold mb-3 flex items-center text-blue-800">
        <Eye className="w-5 h-5 mr-2 text-blue-600" />
        Competition Strategy Tips
      </h3>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white p-3 rounded-md shadow-sm">
          <h4 className="font-semibold text-sm mb-2 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-green-600" />
            Pricing Strategy
          </h4>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>Monitor competitor prices and adjust yours strategically</li>
            <li>Consider promotional discounts during low seasons</li>
          </ul>
        </div>
        <div className="bg-white p-3 rounded-md shadow-sm">
          <h4 className="font-semibold text-sm mb-2 flex items-center">
            <Building className="w-4 h-4 mr-1 text-amber-600" />
            Facilities & Services
          </h4>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>Offer unique facilities that your competitors don't have</li>
            <li>Focus on maintaining higher quality and better reviews</li>
          </ul>
        </div>
        <div className="bg-white p-3 rounded-md shadow-sm">
          <h4 className="font-semibold text-sm mb-2 flex items-center">
            <TrendingUp className="w-4 h-4 mr-1 text-purple-600" />
            Marketing Tactics
          </h4>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>Run targeted marketing campaigns to steal customers</li>
            <li>
              During competitor marketing campaigns, consider price promotions
            </li>
          </ul>
        </div>
        <div className="bg-white p-3 rounded-md shadow-sm">
          <h4 className="font-semibold text-sm mb-2 flex items-center">
            <BarChart2 className="w-4 h-4 mr-1 text-blue-600" />
            Growth Strategy
          </h4>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>
              Expand your room capacity when occupancy rates are consistently
              high
            </li>
            <li>
              Launch seasonal promotions to maintain high occupancy year-round
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CompetitorManagerTab;
